import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './HomeFrota.module.css';
import { API_BASE_URL } from '../../config';
import PageHeader from '../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';

import { FiTruck, FiTool, FiPlusSquare, FiCheckCircle, FiCalendar, FiBarChart2, FiDollarSign, FiAlertTriangle } from 'react-icons/fi';
import { FaWrench } from "react-icons/fa";

const navItems = [
  { to: "/frota/veiculos", icon: <FiTruck />, title: "Listar Veículos" },
  { to: "/frota/cadastro-veiculo", icon: <FiPlusSquare />, title: "Cadastrar Veículo" },
  { to: "/frota/manutencao", icon: <FaWrench />, title: "Manutenções" },
  { to: "/frota/despesasGerais", icon: <FiDollarSign />, title: "Despesas Gerais" },
  { to: "/frota/vencimentos", icon: <FiAlertTriangle />, title: "Alertas e Vencimentos" },
];

const VehicleStatusChart = ({ stats }) => {
  const chartData = useMemo(() => [
    { label: 'Disponíveis', value: stats?.disponiveis || 0, icon: <FiCheckCircle /> },
    { label: 'Em Manutenção', value: stats?.emManutencao || 0, icon: <FiTool /> },
    { label: 'Agendadas', value: stats?.agendadas || 0, icon: <FiCalendar /> },
  ], [stats]);

  const maxValue = Math.max(...chartData.map(d => d.value), 1); 

  return (
    <div className={styles.frotaChartContainer}>
      <div className={styles.frotaChartHeader}>
        <FiBarChart2 />
        <h3>Status da Frota</h3>
      </div>
      <div className={styles.frotaChartArea}>
        {chartData.map(item => (
          <div key={item.label} className={styles.frotaChartBarWrapper}>
            <div className={styles.frotaChartBarValue}>{item.value}</div>
            <div className={styles.frotaChartBarTrack}>
              <div className={styles.frotaChartBar} style={{ height: `${(item.value / maxValue) * 100}%` }}></div>
            </div>
            <div className={styles.frotaChartBarLabel}>
              {item.icon}
              <span>{item.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HomeFrota = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const MIN_LOADING_TIME = 300; 
    const startTime = Date.now();

    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token de autenticação não encontrado.');

        const response = await fetch(`${API_BASE_URL}/api/frota/stats`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Falha ao buscar os dados do painel.');
        }

        const data = await response.json();
        setStats(data);
      } catch (err) {
        setError(err.message);
      } finally {
        const endTime = Date.now();
        const duration = endTime - startTime;
        const remainingTime = MIN_LOADING_TIME - duration;

        if (remainingTime > 0) {
          setTimeout(() => setLoading(false), remainingTime);
        } else {
          setLoading(false);
        }
      }
    };

    fetchStats();
  }, []);

  return (
    <div className={styles.container}>
      {loading && <LoadingOverlay text="Carregando..." />}

      {!loading && (
        <>
          <PageHeader 
            title="Painel de Frota" 
            subtitle="Selecione uma ação ou visualize os indicadores abaixo."
            backTo="/"
          />
          
          <div className={styles.contentBlock}>
              <h2 className={styles.sectionTitle}>Ações Rápidas</h2>
              <div className={styles.actionsGrid}>
                {navItems.map(item => (
                  <Link to={item.to} key={item.to} className={styles.actionCard}>
                    <div className={styles.actionCardIcon}>{item.icon}</div>
                    <span className={styles.actionCardTitle}>{item.title}</span>
                  </Link>
                ))}
              </div>
          </div>

          <div className={styles.contentBlock}>
              <h2 className={styles.sectionTitle}>Indicadores</h2>
              
              {error && <p className={styles.errorText}>{error}</p>}

              {!error && (
                  <div className={styles.frotaDataGrid}>
                      <div className={styles.frotaTotalCard}>
                          <span className={styles.totalCardLabel}>Total de Veículos</span>
                          <span className={styles.totalCardValue}>{stats?.totalVeiculos || 0}</span>
                      </div>
                      <VehicleStatusChart stats={stats} />
                  </div>
              )}
          </div>
        </>
      )}
    </div>
  );
};

export default HomeFrota;